import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/controller";
import { logClientEvent } from "@/telemetry";
import { useUISlice } from "@/models/useUISlice";
import { Transcript } from "@/types";

const ThumbsUpIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 10v12" />
    <path d="M15 5.88L14 10h5.83a2 2 0 011.92 2.56l-2.33 8A2 2 0 0117.5 22H4a2 2 0 01-2-2v-8a2 2 0 012-2h2.76a2 2 0 001.79-1.11L12 2a3.13 3.13 0 013 3.88z" />
  </svg>
);

const ThumbsDownIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 14V2" />
    <path d="M9 18.12L10 14H4.17a2 2 0 01-1.92-2.56l2.33-8A2 2 0 016.5 2H20a2 2 0 012 2v8a2 2 0 01-2 2h-2.76a2 2 0 00-1.79 1.11L12 22a3.13 3.13 0 01-3-3.88z" />
  </svg>
);

export default function TranscriptRatingWidget({
  transcriptId,
  transcript,
  offerAccuracy = false,
}: {
  transcriptId: string;
  transcript: Transcript;
  // A free-model draft: a thumbs-down is answered with the accurate version.
  offerAccuracy?: boolean;
}) {
  const { t } = useTranslation();
  const [rating, setRating] = useState<"good" | "bad" | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const alreadyRated = useAppStore((state) =>
    state.ratedTranscriptIds.includes(transcriptId),
  );

  const isCompleted = transcript?.status === "transcription_completed";
  const hasSegments = transcript?.segments?.length > 0;
  const isTooOld =
    Date.now() - new Date(transcript?.createdAt).getTime() >
    14 * 24 * 60 * 60 * 1000;

  const showAccuracyOffer = rating === "bad" && offerAccuracy;

  useEffect(() => {
    if (showAccuracyOffer) return;
    if (rating === "good" || rating === "bad") {
      const timer = setTimeout(() => setDismissed(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [rating, showAccuracyOffer]);

  if (!isCompleted || !hasSegments || dismissed || isTooOld) {
    return null;
  }

  if (!rating && alreadyRated) {
    return null;
  }

  const handleGood = () => {
    setRating("good");
    useAppStore.getState().markTranscriptAsRated(transcriptId);
    logClientEvent("transcript.ratingSubmitted", {
      transcriptId,
      rating: "good",
    });
  };

  const handleBad = () => {
    setRating("bad");
    useAppStore.getState().markTranscriptAsRated(transcriptId);
    logClientEvent("transcript.ratingSubmitted", {
      transcriptId,
      rating: "bad",
      transcriptionProvider: transcript?.transcriptionProvider,
    });
  };

  if (showAccuracyOffer) {
    return (
      <div
        data-testid="transcript-rating-accuracy-offer"
        className="glass mx-4 mt-6 p-5 rounded-3xl animate-in fade-in duration-300"
      >
        <p className="text-[15px] font-semibold text-[var(--text-primary)]">
          {t("transcriptRating.accuracyOfferTitle")}
        </p>
        <p className="mt-1 text-[14px] leading-snug text-[var(--text-secondary)]">
          {t("transcriptRating.accuracyOfferBody")}
        </p>
        <button
          type="button"
          data-action-id="transcript.rating.accuracyOffer"
          onClick={() => {
            logClientEvent("transcript.ratingOfferSelected", {
              display: transcript.isFullTranscript ? "full" : "partial",
            });
            useUISlice.getState().setPaywallVisible({
              utmSource: "transcript --> bad rating",
            });
          }}
          className="btn btn-primary mt-4 w-full text-[15px] active:scale-[0.97] transition-all"
        >
          {t("transcriptRating.accuracyOfferCta")}
        </button>
      </div>
    );
  }

  if (rating === "good" || rating === "bad") {
    return (
      <div className="glass mx-4 mt-6 p-5 rounded-3xl text-center animate-in fade-in duration-300">
        <p className="text-[14px] text-[var(--text-secondary)]">
          {t("transcriptRating.thanks")}
        </p>
      </div>
    );
  }

  return (
    <div className="glass mx-4 mt-6 p-5 rounded-3xl flex items-center justify-between animate-in fade-in duration-300">
      <p className="text-[14px] text-[var(--text-secondary)]">
        {t("transcriptRating.title")}
      </p>
      <div className="flex items-center gap-2">
        <button
          data-action-id="transcript.rating.good"
          onClick={handleGood}
          className="w-10 h-10 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--glass-bg-hover)] active:scale-90 transition-all"
        >
          <ThumbsUpIcon size={20} />
        </button>
        <button
          data-action-id="transcript.rating.bad"
          onClick={handleBad}
          className="w-10 h-10 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--glass-bg-hover)] active:scale-90 transition-all"
        >
          <ThumbsDownIcon size={20} />
        </button>
      </div>
    </div>
  );
}
